import type { BusinessRuleErrorEnum } from "./business-rule-error.enum";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useModalHubStore } from "~/components/modal-hub";

export type IBusinessRulesActionMapped = "updateItemQuantity" | "checkout";

export const useBusinessRulesValidation = () => {
  const { t } = useLocale();

  const basketStore = useBasketStore();
  const modalHub = useModalHubStore();

  const resolveBasketException = (
    action: IBusinessRulesActionMapped,
    error: any,
  ) => {
    const itemName =
      basketStore.items.find((item) => item.itemId === error?.data?.item_id)
        ?.name ?? "";

    const mapErrors: Record<BusinessRuleErrorEnum | string, string> = {
      gift_card_could_not_be_applied: t(
        "composables.use_business_rules_validation.resolve_basket_exception.gift_card_could_not_be_applied",
      ),
      store_not_accepting_orders: t(
        "composables.use_business_rules_validation.resolve_basket_exception.store_not_accepting_orders",
      ),
      item_not_found: t(
        "composables.use_business_rules_validation.resolve_basket_exception.item_not_found",
        { itemName },
      ),
      item_out_of_stock: t(
        "composables.use_business_rules_validation.resolve_basket_exception.item_out_of_stock",
        { itemName },
      ),
      loyalty_free_item_not_found: t(
        "composables.use_business_rules_validation.resolve_basket_exception.loyalty_free_item_not_found",
      ),
      maximum_order_capacity: t(
        "composables.use_business_rules_validation.resolve_basket_exception.maximum_order_capacity",
      ),
      loyalty_program_no_longer_available:
        action === "updateItemQuantity"
          ? t(
              "composables.use_business_rules_validation.resolve_basket_exception.loyalty_program_no_longer_available_update_item",
            )
          : t(
              "composables.use_business_rules_validation.resolve_basket_exception.loyalty_program_no_longer_available",
            ),
      address_missing: t(
        "composables.use_business_rules_validation.resolve_basket_exception.address_missing",
      ),
    };

    const errorFields = error?.data?.fields;

    if (errorFields) {
      const fieldErrors: Record<string, string> = {
        scheduledTo: t(
          "composables.use_business_rules_validation.resolve_checkout_exception.scheduled_to_past",
        ),
      };

      const mappedFieldErrorMessage = Object.keys(errorFields)
        .map((key) => fieldErrors[key])
        .join("\n");

      if (error.statusCode === 422 && mappedFieldErrorMessage) {
        modalHub.showError(
          t(
            "composables.use_business_rules_validation.resolve_checkout_exception.modal_title",
          ),
          mappedFieldErrorMessage,
          t(
            "composables.use_business_rules_validation.resolve_checkout_exception.modal_cta_label",
          ),
        );
        return true;
      }

      return false;
    }

    const mappedErrorMessage = mapErrors[error?.statusMessage] ?? error.message;

    if (error?.statusCode === 422 && mappedErrorMessage) {
      modalHub.showError(
        t(
          "composables.use_business_rules_validation.resolve_basket_exception.modal_title",
        ),
        mappedErrorMessage,
        t(
          "composables.use_business_rules_validation.resolve_basket_exception.modal_cta_label",
        ),
      );

      return true;
    }

    return false;
  };

  return { resolveBasketException };
};
